import React, { useRef } from "react";
import { Line } from "react-chartjs-2";
import { Chart as ChartJS, registerables } from "chart.js";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

ChartJS.register(...registerables);

const utahData = "/img/headerlogo.png";
const kpi = "/img/KPI-DASH 2.png";
const bodex = "/img/bodexLogo.png";
const pdf = "/img/pdf1.png";

const EngTable14 = ({ data }) => {
  const pdfRef = useRef();

  // Extract chart data
  const years = data.map((item) => item.year);
  const values = data.map((item) => Number(item.employment));

  // Summary calculations
  const avg = (values.reduce((a, b) => a + b, 0) / values.length).toFixed(2);
  const maxValue = Math.max(...values);
  const minValue = Math.min(...values);
  const maxYear = years[values.indexOf(maxValue)];
  const minYear = years[values.indexOf(minValue)];

  const trend =
    values[values.length - 1] > values[0]
      ? "Growing 📈"
      : values[values.length - 1] < values[0]
        ? "Declining 📉"
        : "Stable ➖";

  const chartData = {
    labels: years,
    datasets: [
      {
        label: "Employment (Bachelor’s Degree)",
        data: values,
        borderColor: "#F8BBD0",
        backgroundColor: "#F8BBD020",
        pointBackgroundColor: "#F8BBD0",
        borderWidth: 3,
        tension: 0.35,
      },
    ],
  };

  const options = {
    responsive: true,
    plugins: {
      legend: { position: "top" },
      title: {
        display: true,
        text: "Employment Growth with Bachelor's Degree (2018–2024)",
        font: { size: 18, weight: "bold" },
      },
    },
    scales: {
      y: { title: { display: true, text: "Employment Count" } },
      x: { title: { display: true, text: "Year" } },
    },
  };

  // PDF EXPORT
  const downloadPDF = () => {
    const input = pdfRef.current;

    html2canvas(input, { scale: 2 }).then((canvas) => {
      const pdf = new jsPDF("p", "mm", "a4");

      const width = pdf.internal.pageSize.getWidth() - 10;
      const height = (canvas.height * width) / canvas.width;

      // HEADER
      pdf.addImage(utahData, "PNG", 10, 5, 30, 25);
      pdf.setFontSize(22);
      pdf.text("UTahData.org", 50, 20);
      pdf.line(10, 32, 200, 32);

      // CONTENT
      pdf.addImage(canvas.toDataURL("image/png"), "PNG", 5, 35, width, height);

      // FOOTER
      pdf.line(10, 260, 200, 260);
      pdf.addImage(utahData, "PNG", 20, 265, 18, 14);
      pdf.setFontSize(10);
      pdf.text("Built by", 45, 276);
      pdf.addImage(bodex, "PNG", 58, 268, 40, 10);
      pdf.text("using", 105, 276);
      pdf.addImage(kpi, "PNG", 118, 265, 18, 18);

      pdf.text(`Version: 20250916.v3.198`, 150, 276);
      pdf.text(`Exported: ${new Date().toLocaleDateString()}`, 145, 292);
      pdf.text(`Source: ${window.location.href}`, 10, 292);

      pdf.save("EngTable14.pdf");
    });
  };

  return (
    <>
      <div ref={pdfRef} className="p-6 bg-white shadow-lg rounded-xl">
        <h2 className="text-2xl font-bold text-center mb-4 text-gray-900">
          Employment Growth with Bachelor's Degree (2018–2024)
        </h2>

        {/* KPI SUMMARY BLOCKS */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
          <div className="p-4 rounded-xl bg-purple-50 shadow">
            <p className="text-purple-800 font-semibold">📈 Growth Trend</p>
            <p className="text-gray-900">{trend}</p>
          </div>
          <div className="p-4 rounded-xl bg-green-50 shadow">
            <p className="text-green-800 font-semibold">📊 Average</p>
            <p className="text-gray-900">{avg.toLocaleString("en-US")}</p>
          </div>
          <div className="p-4 rounded-xl bg-blue-50 shadow">
            <p className="text-blue-800 font-semibold">🏆 Highest Year</p>
            <p className="text-gray-900">
              {maxYear} — {maxValue.toLocaleString("en-US")}
            </p>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* TABLE */}
          <div className="flex flex-col justify-center">
            <div className="overflow-x-auto">
              <table className="w-full text-left shadow-md border rounded-lg overflow-hidden">
                <thead className="bg-gray-800 text-white">
                  <tr>
                    <th className="p-2">Year</th>
                    <th className="p-2">Employment</th>
                    <th className="p-2">Growth %</th>
                  </tr>
                </thead>
                <tbody>
                  {data.map((row, index) => (
                    <tr key={index} className="hover:bg-gray-100 transition">
                      <td className="p-2 font-semibold">{row.year}</td>
                      <td className="p-2">
                        {Number(row.employment).toLocaleString("en-US")}
                      </td>
                      <td className="p-2">{row.growth_percentage ?? "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* LINE CHART */}
          <div className="flex flex-col w-full overflow-x-auto bg-white rounded-lg shadow-md">
            <div className="min-w-[600px] w-full h-96 p-4">
              <Line data={chartData} options={options} />
            </div>
          </div>
        </div>
      </div>

      {/* PDF BUTTON */}
      <div className="flex justify-center mt-4">
        <button
          onClick={downloadPDF}
          className="bg-red-700 hover:bg-red-900 px-4 py-2 text-white rounded-lg flex items-center gap-2 shadow-lg transition-transform hover:scale-105"
        >
          <span>Download as PDF</span>
          <img className="w-[1.5rem]" src={pdf} alt="pdf-icon" />
        </button>
      </div>
    </>
  );
};

export default EngTable14;
